import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LANDING } from "@/constants/testIds";
import { readConsent } from "@/utils/consent";
import {
  trackLead, trackInitiateCheckout, trackQualifiedLead,
  genEventId, getMetaCookies, setPixelUserIdentity, getCurrentExternalId,
} from "@/utils/pixel";
import { setUserToken } from "@/utils/userAuth";
import RegisterStep1 from "./register/RegisterStep1";
import RegisterStep2 from "./register/RegisterStep2";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Two-step lead-gen + password-setup dialog.
 * - Step 1 collects identity + consents → POST /users/register (no password yet)
 * - Step 2 sets the password → POST /users/set-password
 * State lives here; presentational steps are in ./register/*.
 */
export default function RegisterDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", password2: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [redirectTarget, setRedirectTarget] = useState("/corso");

  // On open: reset state + fire InitiateCheckout (browser Pixel + server CAPI)
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setStatus("idle");
    setError("");
    const consent = readConsent();
    const eid = genEventId();
    if (consent.marketing) trackInitiateCheckout(eid);
    const meta = getMetaCookies();
    const externalId = getCurrentExternalId();
    axios.post(`${API}/events/initiate-checkout`, {
      event_id: eid,
      consent_marketing: !!consent.marketing,
      landing_page_url: typeof window !== "undefined" ? window.location.href : null,
      fbp: meta.fbp, fbc: meta.fbc,
      external_id: externalId || null,
    }).catch(() => {});
  }, [open]);

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (!acceptTerms) {
      setError("Devi accettare i termini per accedere alla prima lezione gratuita");
      return;
    }
    setStatus("loading");
    setError("");
    const consent = readConsent();
    const meta = getMetaCookies();
    try {
      const { data } = await axios.post(`${API}/users/register`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        consent_marketing: acceptMarketing || !!consent.marketing,
        consent_terms: acceptTerms,
        landing_page_url: window.location.href,
        fbp: meta.fbp, fbc: meta.fbc,
      });
      setUserToken(data.access_token, true);
      setRedirectTarget(data.redirect_to || "/corso");
      // Propagate identity to Pixel BEFORE firing Lead so external_id matches CAPI.
      try {
        const uid = getCurrentExternalId();
        if (uid) setPixelUserIdentity(uid);
      } catch { /* ignore */ }
      if (acceptMarketing || consent.marketing) trackLead(genEventId());
      setStatus("idle");
      setStep(2);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Errore. Riprova.";
      setError(typeof detail === "string" ? detail : "Dati non validi");
      setStatus("error");
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      setError("La password deve essere di almeno 8 caratteri.");
      setStatus("error");
      return;
    }
    if (form.password !== form.password2) {
      setError("Le due password non coincidono. Reinseriscile.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      const tok = (() => { try { return localStorage.getItem("np_user_token") || ""; } catch { return ""; } })();
      const meta = getMetaCookies();
      await axios.post(`${API}/users/set-password`, {
        password: form.password,
        fbp: meta.fbp, fbc: meta.fbc,
        landing_page_url: typeof window !== "undefined" ? window.location.href : null,
      }, { headers: { Authorization: `Bearer ${tok}` } });
      // Fire QualifiedLead client-side (deduped server-side via deterministic event_id).
      try {
        const me = await axios.get(`${API}/users/me`, { headers: { Authorization: `Bearer ${tok}` } });
        const consent = readConsent();
        if (me?.data?.id) {
          try { setPixelUserIdentity(me.data.id); } catch { /* ignore */ }
        }
        if (consent.marketing && me?.data?.id) {
          trackQualifiedLead(`qualifiedlead_${me.data.id}`, { value: 2, currency: "EUR" });
        }
      } catch { /* ignore */ }
      setStatus("success");
      setTimeout(() => navigate(redirectTarget), 350);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossibile salvare la password.";
      setError(typeof detail === "string" ? detail : "Errore");
      setStatus("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={LANDING.leadDialog} className="sm:max-w-[480px] rounded-2xl border-0 p-0 overflow-hidden bg-white">
        <div className="h-2 bg-tension w-full" />
        <div className="px-7 pt-6 pb-7">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-2xl" style={{ fontFamily: "Merriweather, serif", color: "var(--np-primary)" }}>
              {step === 1 ? "Ottieni la prima lezione gratis" : "Crea la tua password"}
            </DialogTitle>
            <DialogDescription className="text-[15px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
              {step === 1 ? (
                <>Lascia i tuoi contatti e accedi subito alla <strong>Lezione 1</strong> — gratuita, senza inserire la carta di credito.</>
              ) : (
                <>Ultimo passo: imposta una password per rientrare nel corso da qualsiasi dispositivo via <a href="/login" className="underline">/login</a>.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {step === 1 ? (
            <RegisterStep1
              form={form} setForm={setForm}
              acceptTerms={acceptTerms} setAcceptTerms={setAcceptTerms}
              acceptMarketing={acceptMarketing} setAcceptMarketing={setAcceptMarketing}
              status={status} error={error}
              onSubmit={submitStep1}
            />
          ) : (
            <RegisterStep2
              form={form} setForm={setForm}
              status={status} error={error}
              onSubmit={submitStep2}
              onPasswordTyped={() => { if (status === "error") setStatus("idle"); }}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
